import { Graphics } from "pixi.js"
import { stageViewport } from "../../../../canvas_layout"

// THE MOMENT A WAVE LANDS.
//
// The biggest beat in the camp's loop was its quietest: the countdown dial reached zero, vanished,
// and the wave badge grew into its slot. Both of those are 118px of HUD in one corner, and the
// player is looking at the middle of the screen. Nothing in the WORLD said anything had happened.
//
// This is the world's half of it: one hit of warm light thrown in from the edges of the screen,
// paired with a shake impulse (gui_manager fires both together).
//
// EDGE-WEIGHTED, and that is the whole design. A full-screen white flash is the obvious thing to
// reach for and it is exactly wrong here: the instant a wave lands is the instant the player most
// needs to read the field, and a wash over the middle of the screen takes that away to tell them
// something the badge is already saying. The light comes in from the rim and leaves the centre
// alone -- it is felt in peripheral vision, which is where a thing arriving behind you belongs.
//
// No filters, like everything else drawn here: the falloff is a handful of nested frames at
// decreasing alpha, because the hardware target cannot spare a blur pass.

export const WAVE_FLASH_MS = 520

// Where in its life the flash peaks. Fast in, slow out -- a symmetrical envelope reads as a fade
// rather than as an impact.
const PEAK_AT = 0.16

// The falloff is built from stacked layers, each a full-screen fill with an ELLIPTICAL hole in it.
// A point near the rim falls outside every hole and so takes every layer; a point near the middle
// falls inside them all and takes none. The gradient is therefore the ACCUMULATION of equal alphas
// rather than a ramp of different ones -- which is what stops it reading as a set of steps.
//
// The first attempt stacked rectangular frames at a falloff curve: six hard-edged boxes, plainly
// visible as boxes. Light does not arrive in rectangles.
const BANDS = 9
// Where the innermost hole sits, and where the outermost reaches, as a fraction of the half-width
// and half-height. The first is the clear zone the fight stays readable in; the second runs past
// the corners so the rim gets every layer.
//
// 0.55 is deliberately generous -- it leaves the whole of a camp's lit clearing untouched and puts
// the wash in the outer half only. At 0.34 the light reached well inside the clearing and the grass
// went brown at the peak, which is the failure this effect exists to avoid: the frame a wave lands
// is the frame the player most needs to read the ground they are standing on.
const CLEAR_ZONE = 0.55
const OUTER_REACH = 1.05

const FLASH_COLOR = 0xff5a2a
// The hot line right at the rim, which lands and goes almost at once: it is what makes the hit read
// as a strike rather than as a glow coming up.
const RIM_COLOR = 0xffd9a0

/**
 * The envelope, 0..1 over the effect's life. Exported pure: the shape of a hit is worth asserting
 * without a renderer.
 */
export const waveFlashIntensity = (age: number): number => {
    if (!(age > 0) || age >= 1) {
        return 0
    }
    if (age < PEAK_AT) {
        return age / PEAK_AT
    }
    const t = (age - PEAK_AT) / (1 - PEAK_AT)
    return (1 - t) * (1 - t)
}

/**
 * How hard a given wave hits, 0..1.
 *
 * Wave 1 is a knock and the late table is a slam, because the twenty-wave table is the camp's whole
 * arc and the screen should know where in it you are. Clamped, so wave 30 is not thirty times
 * wave 1 -- past the top of the table this stops growing rather than becoming a seizure.
 */
export const waveFlashStrength = (wave: number): number =>
    Math.max(0, Math.min(1, 0.55 + Math.max(0, wave - 1) * 0.035))

export class WaveLandingFlash extends Graphics {
    private ageMs = WAVE_FLASH_MS
    private strength = 0

    constructor() {
        super()
        this.visible = false
    }

    /** A wave has landed. Restarts the hit if one is somehow still running. */
    public strike = (wave: number) => {
        this.strength = waveFlashStrength(wave)
        this.ageMs = 0
        this.visible = true
    }

    /** Test seam: how far through the hit we are, 0..1 (1 once it is over). */
    public progress = (): number => Math.min(1, this.ageMs / WAVE_FLASH_MS)

    public animate = (deltaMs: number) => {
        if (!this.visible) {
            return
        }
        this.ageMs += deltaMs
        if (this.ageMs >= WAVE_FLASH_MS) {
            this.visible = false
            this.clear()
            return
        }
        this.redraw(waveFlashIntensity(this.ageMs / WAVE_FLASH_MS) * this.strength)
    }

    private redraw = (power: number) => {
        this.clear()
        if (!(power > 0)) {
            return
        }
        const view = stageViewport()
        const cx = view.left + view.width / 2
        const cy = view.top + view.height / 2
        const alpha = (0.46 * power) / BANDS

        for (let band = 0; band < BANDS; band++) {
            const at = band / (BANDS - 1)
            const spread = CLEAR_ZONE + (OUTER_REACH - CLEAR_ZONE) * at
            this.beginFill(FLASH_COLOR, alpha)
            this.drawRect(view.left, view.top, view.width, view.height)
            this.beginHole()
            this.drawEllipse(cx, cy, (view.width / 2) * spread, (view.height / 2) * spread)
            this.endHole()
            this.endFill()
        }

        // The rim line, on only for the first fifth of the hit.
        const rim = Math.max(0, power * 1.6 - 0.6)
        if (rim > 0) {
            this.lineStyle({ width: 3, color: RIM_COLOR, alpha: Math.min(0.75, rim) })
            this.drawRect(view.left + 1.5, view.top + 1.5, view.width - 3, view.height - 3)
            this.lineStyle(0)
        }
    }
}
